import {
  useAddCustomerSuccessNoteMutation,
  useCustomerAgencyQuery,
  useCustomerSuccessNotesQuery,
} from "@/api/customerSuccessApi";
import Button from "@/components/Common/Button";
import InputField from "@/components/Common/forms/InputField";
import Loader from "@/components/Common/kpi/Loader";
import Modal from "@/components/Common/Modal";
import {FULL_DATE_FORMAT} from "@/store/constants";
import {customerSuccessModalAgencyIdAtom} from "@/store/jotaiAtoms";
import dayjs from "dayjs";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const EYEBROW_CLASS = "text-neutral-grey-dark text-2xs font-bold tracking-wide uppercase";

const CustomerSuccessModal: FC = () => {
  const {t} = useTranslation("customer_success");
  const [agencyId, setAgencyId] = useAtom(customerSuccessModalAgencyIdAtom);
  const [noteText, setNoteText] = useState("");
  const agencyQuery = useCustomerAgencyQuery(agencyId!);
  const notesQuery = useCustomerSuccessNotesQuery(agencyId!);
  const {isPending: isAddingNote, mutate: addNote} = useAddCustomerSuccessNoteMutation(agencyId!);

  const agency = agencyQuery.data;

  return (
    <Modal
      backgroundClassName="max-w-160"
      customBottomRow={<></>}
      modalTitle={agency?.agency_name ?? t("title")}
      onClose={() => setAgencyId(null)}>
      {!agency ? (
        <Loader styles={{containerClassName: "my-6"}} />
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <span className={EYEBROW_CLASS}>{t("show_all_notes")}</span>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <InputField
                className="h-10 text-sm"
                containerClassName="grow"
                onChange={setNoteText}
                placeholder={t("add_note_placeholder")}
                value={noteText}
              />
              <Button
                buttonStyle="Gradient"
                className="h-10 shrink-0 px-5 text-sm"
                isDisabled={!noteText.trim()}
                isLoading={isAddingNote}
                onClick={() => addNote(noteText.trim(), {onSuccess: () => setNoteText("")})}>
                {t("add_note")}
              </Button>
            </div>
            {notesQuery.isLoading && <Loader styles={{containerClassName: "my-2"}} />}
            {!notesQuery.isLoading && !notesQuery.data?.length && (
              <span className="text-neutral-grey-dark text-sm">{t("no_notes")}</span>
            )}
            {!!notesQuery.data?.length && (
              <div className="flex max-h-80 flex-col gap-2.5 overflow-y-auto">
                {notesQuery.data.map(note => (
                  <div key={note.id} className="border-neutral-white-dark rounded-xl border p-4">
                    <div className="text-neutral-grey-dark text-2xs flex flex-wrap items-center gap-2">
                      <span className="font-bold">{note.author_name ?? "—"}</span>
                      <span>{dayjs(note.created).format(FULL_DATE_FORMAT)}</span>
                    </div>
                    <p className="text-neutral-black mt-1 text-sm whitespace-pre-line">{note.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default CustomerSuccessModal;
